import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import { debounce } from "@mui/material/utils";
import { GoogleProvider } from "leaflet-geosearch";
import { LatLng } from "leaflet";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, GridLegacy } from "@mui/material";
import ModalControlProps from "models/explorer/modal-control-props";
import parse from "autosuggest-highlight/parse";

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}
const googleMapsOptions = {
  componentRestrictions: { country: "au" },
};

const GoogleMaps = (modalControlProps: ModalControlProps) => {
  const { customSearch, handleCustomSearch } = modalControlProps;
  const [value, setValue] = useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const loaded = useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?fields=formatted_address,name,geometry&key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places&loading=async`,
        document.querySelector("head"),
        "google-maps",
      );
    }

    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          const requestWithRestrict = {
            ...request,
            componentRestrictions: { country: "au" },
          };
          (autocompleteService.current as any).getPlacePredictions(
            requestWithRestrict,
            callback,
          );
        },
        400,
      ),
    [],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService(googleMapsOptions);
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
        // console.log(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      id="google-map-demo"
      fullWidth
      sx={{ my: 1 }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="Start typing to search"
      onChange={(_event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue) {
          const provider = new GoogleProvider({
            apiKey: process.env.REACT_APP_GOOGLE_API_KEY ?? "",
          });
          provider.search({ query: newValue?.description }).then((results) => {
            if (!results[0]) console.error("Couldn't find google map result");
            const result = results[0] ?? { x: 0, y: 0 };
            return handleCustomSearch({
              ...customSearch,
              enabled: true,
              latlong: new LatLng(result.y, result.x),
              type: "google",
            });
          });
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {!loaded.current ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
          label="Search for a location"
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        // console.log(option);
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length,
          ]),
        );
        // console.log(matches);

        return (
          <li {...props}>
            <GridLegacy container alignItems="center">
              <GridLegacy item sx={{ display: "flex", width: 44 }}>
                {/* <LocationOnIcon sx={{ color: 'text.secondary' }} /> */}
              </GridLegacy>
              <GridLegacy item sx={{ wordWrap: "break-word" }}>
                {parts.map(
                  (
                    part: {
                      highlight: boolean;
                      text: string | null | undefined;
                    },
                    index: React.Key | null | undefined,
                  ) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                    >
                      {part.text}
                    </Box>
                  ),
                )}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </GridLegacy>
            </GridLegacy>
          </li>
        );
      }}
    />
  );
};

export default GoogleMaps;
